import { useEffect, useMemo, useState } from 'react';
import { EventStack, type BriefingEvent } from '@/features/briefing/BriefingCard';
import { apiGet, ApiError } from '@/lib/api/client';
import { useObjectsByType, type XaviorObjectBase } from '@/features/objects/hooks';
import type { EventsCardData, EventsCardEvent } from './types';

/**
 * Schedule card — the canonical "event stack + discussion" format: the agent's
 * short text reply carries the discussion, and this card is just the stacked
 * event rows (shared `EventStack` from the Today/briefing card) + an
 * Open-calendar link.
 *
 * The old format (week-density strip + day-grouped agenda with time rails) is
 * retired — product call 2026-06-09: one schedule visual language everywhere.
 * `hideStrip` is accepted for back-compat (the strip no longer exists).
 *
 * ── Card ↔ calendar alignment (fix/chat-card-live-view) ──────────────────────
 * This card used to render its FROZEN agent-claim snapshot (`card.data.events`)
 * verbatim, while the calendar grid runs a LIVE `useObjectsByType('event', …)`
 * query — so a later delete/edit/recurrence change made the two diverge.
 *
 * Two render modes, chosen by whether the card carries `objectIds`:
 *   • LIVE  ({@link EventsResultCardLive}) — new cards carry `objectIds`. The card
 *     re-reads those objects from the SAME TanStack objects cache the grid uses
 *     (server-side recurrence expansion, the same the calendar agrees with), so
 *     EVERY existing invalidation channel (tool-receipt → `scheduleObjectList
 *     Invalidation('event')`, stream-end, optimistic CRUD) reconciles the card
 *     exactly like the grid: a deleted event drops out, an edit shows the new
 *     value, recurrence expands consistently.
 *   • LEGACY ({@link EventsResultCardLegacy}) — cards persisted BEFORE `objectIds`
 *     existed have no ids, only the embedded snapshot. They keep the original
 *     behavior: render `card.data.events` (now with a defensive tombstone filter)
 *     + the mount-once `GET /events/{id}` 404 staleness check (#4879). Historical
 *     transcripts must not break, so this path is preserved unchanged.
 */

/** Parse an event timestamp to a LOCAL Date. A bare `YYYY-MM-DD` (all-day or
 *  date-only value) is constructed in local time rather than via `new Date()`,
 *  which would treat it as UTC midnight and shift it to the previous day in
 *  negative-UTC-offset timezones. */
function toLocalDate(s: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(s);
}

/** Recurring occurrences come back from the server with a synthetic id
 *  `${masterId}:${ISO}`. Strip the occurrence suffix so the membership filter +
 *  click-through target the real workspace object. Mirrors the x-agent tool's
 *  `masterObjectId`. Tolerates a full datetime occurrence or a bare date. */
function masterObjectId(id: string): string {
  return id.replace(/:\d{4}-\d{2}-\d{2}(T.*)?$/, '');
}

/** A tombstone the live calendar would have reconciled away. Embedded snapshots
 *  rarely carry these fields, but new writers (and the live query) may — so we
 *  drop them defensively rather than ever render a ghost. Accepts `unknown` so
 *  both the live event object and the embedded card-event shape pass without a
 *  cast. */
function isEventTombstone(e: unknown): boolean {
  const o = (e ?? {}) as { archived?: unknown; cancelledOccurrence?: unknown; status?: unknown };
  return o.archived === true || o.cancelledOccurrence === true || o.status === 'cancelled';
}

/** Sort + map an {@link EventsCardEvent} list to the {@link BriefingEvent} rows
 *  the shared `EventStack` renders. Shared by both render modes so the visual
 *  output is identical regardless of where the events came from. */
function toBriefingEvents(events: EventsCardEvent[]): BriefingEvent[] {
  return [...events]
    .sort((a, b) => toLocalDate(a.start).getTime() - toLocalDate(b.start).getTime())
    .map((ev) => ({
      // Click-through wants the real workspace object id (recurring occurrences
      // carry a `${masterId}:${occurrenceISO}` row id); deep-link uses `start`
      // so the calendar lands on the clicked OCCURRENCE date, not the master.
      id: ev.objectId ?? ev.id,
      title: ev.title,
      start: ev.start,
      end: ev.end ?? null,
      allDay: ev.allDay === true,
      attendees: (ev.attendees ?? []).map((a) => a.name || a.email).filter(Boolean),
    }));
}

/** A live event object as returned (flattened) by GET /events / useObjectsByType. */
interface LiveEventObject extends XaviorObjectBase {
  title?: string;
  startAt?: string;
  endAt?: string;
  allDay?: boolean;
  location?: string;
  archived?: boolean;
  cancelledOccurrence?: boolean;
  status?: string;
  attendees?: Array<{ email?: string; displayName?: string; name?: string; value?: string; photoUrl?: string | null }>;
}

/** Map a live event object to the card's {@link EventsCardEvent} shape. */
function liveEventToCardEvent(e: LiveEventObject): EventsCardEvent {
  const rowId = String(e.id);
  return {
    id: rowId,
    objectId: masterObjectId(rowId),
    title: e.title || 'Untitled event',
    start: e.startAt ?? '',
    end: e.endAt,
    allDay: e.allDay === true,
    location: e.location,
    attendees: (e.attendees ?? []).map((a) => ({
      name: a.name || a.displayName,
      email: a.email ?? a.value ?? '',
      photoUrl: a.photoUrl ?? null,
    })),
  };
}

interface EventsResultCardProps {
  data: EventsCardData;
  onOpenEvent: (e: { id: string; start: string }) => void;
  onOpenRange: () => void;
  /** Back-compat no-op — the retired week-density strip is gone entirely. */
  hideStrip?: boolean;
  hideOpenButton?: boolean;
}

/**
 * Dispatcher: a card carrying `objectIds` (the presence of the key, not its
 * length) is a LIVE card; otherwise it is a LEGACY snapshot. Each mode is its own
 * component so the hooks it uses stay unconditional.
 */
export function EventsResultCard(props: EventsResultCardProps) {
  const isLive = Array.isArray(props.data.objectIds);
  return isLive ? <EventsResultCardLive {...props} /> : <EventsResultCardLegacy {...props} />;
}

/** Presentational shell shared by both modes — header, the live `EventStack`,
 *  an optional degraded "deleted" list (legacy only), and the Open-calendar CTA. */
function EventsCardShell({
  rangeLabel,
  totalCount,
  liveEvents,
  deletedEvents,
  onOpenEvent,
  onOpenRange,
  hideOpenButton,
}: {
  rangeLabel: string;
  totalCount: number;
  liveEvents: BriefingEvent[];
  deletedEvents: BriefingEvent[];
  onOpenEvent: (e: { id: string; start: string }) => void;
  onOpenRange: () => void;
  hideOpenButton?: boolean;
}) {
  return (
    <div className="bc-card">
      <div className="bc-header">
        <span>
          Schedule · {rangeLabel} · {totalCount} {totalCount === 1 ? 'event' : 'events'}
        </span>
      </div>
      {totalCount === 0 ? (
        <div className="bc-empty-day">Nothing scheduled — the window is wide open.</div>
      ) : (
        <>
          {liveEvents.length > 0 && (
            <EventStack
              events={liveEvents}
              onOpenEvent={(id, start) => onOpenEvent({ id, start: start ?? '' })}
            />
          )}
          {deletedEvents.length > 0 && (
            <div className="rc-ev-deleted-list" aria-label="Deleted events">
              {deletedEvents.map((e) => (
                <div key={e.id} className="bc-event-row rc-event--deleted" aria-disabled="true">
                  <span className="bc-event-date">
                    <span className="bc-event-wd">{new Date(e.start ?? '').toLocaleString('en-US', { weekday: 'short' })}</span>
                    <span className="bc-event-day">{new Date(e.start ?? '').getDate() || ''}</span>
                    <span className="bc-event-mon">{new Date(e.start ?? '').toLocaleString('en-US', { month: 'short' })}</span>
                  </span>
                  <span className="bc-event-main">
                    <span className="bc-event-title rc-event--deleted__title">{e.title}</span>
                    <span className="bc-event-ctx rc-event--deleted__notice">Event no longer exists</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {!hideOpenButton && (
        <button type="button" className="bc-link bc-link-cal" onClick={onOpenRange}>
          Open calendar <span aria-hidden>→</span>
        </button>
      )}
    </div>
  );
}

/**
 * LIVE mode — read the card's events from the SAME objects cache the calendar
 * grid uses, scoped to the persisted window, then keep only the occurrences whose
 * master object is in the card's `objectIds` set. Server-side recurrence
 * expansion (the default; what the grid agrees with) yields occurrence ids of the
 * form `${masterId}:${ISO}` which `masterObjectId` collapses for membership +
 * click-through. Because the query lives under the `objects.byType('event')`
 * family, every existing invalidation channel reconciles this card exactly like
 * the grid — a delete drops the row, an edit shows the new value, recurrence
 * expands consistently. While the live query is still loading on first mount we
 * seed from the embedded snapshot (tombstone-filtered) so the card never flashes
 * empty; there is no 404 hack and no degraded "deleted" rows — deletion simply
 * removes the event.
 */
function EventsResultCardLive({ data, onOpenEvent, onOpenRange, hideOpenButton }: EventsResultCardProps) {
  const objectIds = useMemo(() => new Set(data.objectIds ?? []), [data.objectIds]);

  const { data: queryData } = useObjectsByType<LiveEventObject>('event', {
    limit: 250,
    startAfter: data.rangeStart,
    startBefore: data.rangeEnd,
    // Server-side recurrence expansion (the calendar's client-side expander
    // mirrors it): occurrences arrive pre-reconciled (cancelled/exception
    // handling applied), so the card agrees with the grid without importing the
    // calendar module (a forbidden features→modules edge).
    enabled: objectIds.size > 0,
  });

  const liveItems = queryData?.items;

  const cardEvents = useMemo<EventsCardEvent[]>(() => {
    if (liveItems) {
      // LIVE: only the occurrences whose master object the agent surfaced, with
      // tombstones dropped (belt-and-suspenders over the server's own filter).
      return liveItems
        .filter((e) => !isEventTombstone(e) && objectIds.has(masterObjectId(String(e.id))))
        .map(liveEventToCardEvent);
    }
    // FIRST-PAINT / fallback: the live query has not resolved yet (or errored).
    // Render the embedded snapshot — tombstone-filtered — so the card paints
    // instantly and never shows a ghost while the cache warms.
    return (data.events ?? []).filter((e) => !isEventTombstone(e));
  }, [liveItems, objectIds, data.events]);

  const liveEvents = useMemo(() => toBriefingEvents(cardEvents), [cardEvents]);

  return (
    <EventsCardShell
      rangeLabel={data.rangeLabel}
      totalCount={liveEvents.length}
      liveEvents={liveEvents}
      deletedEvents={[]}
      onOpenEvent={onOpenEvent}
      onOpenRange={onOpenRange}
      hideOpenButton={hideOpenButton}
    />
  );
}

/**
 * LEGACY mode — cards persisted before `objectIds` existed. Render the embedded
 * snapshot (now with a defensive tombstone filter) and keep the original #4879
 * staleness check: on mount each event id is probed against `GET /events/{id}`;
 * ids that 404 render in a muted/degraded state with click-through blocked. One
 * 404 never blocks the others (`Promise.allSettled`). Preserved verbatim so
 * historical transcripts keep working.
 */
function EventsResultCardLegacy({ data, onOpenEvent, onOpenRange, hideOpenButton }: EventsResultCardProps) {
  // Map + sort once per data change — this card re-renders frequently while a
  // reply streams.
  const events = useMemo<BriefingEvent[]>(
    () => toBriefingEvents((data.events ?? []).filter((e) => !isEventTombstone(e))),
    [data],
  );

  // Track which event IDs have been hard-deleted (404 from the live API).
  // Populated on mount via Promise.allSettled so one 404 never blocks others.
  const [deletedIds, setDeletedIds] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    if (events.length === 0) return;

    const checks = events.map((ev) =>
      apiGet(`/events/${ev.id}`).then(
        () => ({ id: ev.id, deleted: false }),
        (err: unknown) => ({
          id: ev.id,
          deleted: err instanceof ApiError && err.status === 404,
        }),
      ),
    );

    let cancelled = false;
    Promise.allSettled(checks).then((results) => {
      if (cancelled) return;
      const ids = new Set<string>();
      for (const result of results) {
        if (result.status === 'fulfilled' && result.value.deleted) {
          ids.add(result.value.id);
        }
      }
      if (ids.size > 0) setDeletedIds(ids);
    });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Intentionally run once on mount — the snapshot is immutable after creation
  // and we only need to validate stale entries from the persisted card data.

  const liveEvents = useMemo(
    () => events.filter((e) => !deletedIds.has(e.id)),
    [events, deletedIds],
  );
  const deletedEvents = useMemo(
    () => events.filter((e) => deletedIds.has(e.id)),
    [events, deletedIds],
  );

  return (
    <EventsCardShell
      rangeLabel={data.rangeLabel}
      totalCount={events.length}
      liveEvents={liveEvents}
      deletedEvents={deletedEvents}
      onOpenEvent={onOpenEvent}
      onOpenRange={onOpenRange}
      hideOpenButton={hideOpenButton}
    />
  );
}
